import { createTheme, ThemeOptions } from '@mui/material/styles';

const getDesignTokens = (mode: 'light' | 'dark'): ThemeOptions => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          // Light mode colors - Professional Grey Palette
          primary: {
            main: '#6b7280', // Medium Grey
            light: '#9ca3af',
            dark: '#4b5563',
            contrastText: '#ffffff',
          },
          secondary: {
            main: '#64748b', // Slate Grey
            light: '#94a3b8',
            dark: '#475569',
            contrastText: '#ffffff',
          },
          success: {
            main: '#16a34a', // Vibrant Green
            light: '#4ade80',
            dark: '#15803d',
            contrastText: '#ffffff',
          },
          warning: {
            main: '#f97316', // Modern Orange
            light: '#fb923c',
            dark: '#ea580c',
            contrastText: '#ffffff',
          },
          error: {
            main: '#dc2626', // Bold Red
            light: '#ef4444',
            dark: '#b91c1c',
            contrastText: '#ffffff',
          },
          info: {
            main: '#0891b2', // Cyan Blue
            light: '#06b6d4',
            dark: '#0e7490',
            contrastText: '#ffffff',
          },
          background: {
            default: '#f1f5f9', // Brighter Soft Gray
            paper: '#ffffff',
          },
          text: {
            primary: '#0f172a', // Dark Slate
            secondary: '#64748b', // Lighter Medium Slate
          },
          divider: '#e2e8f0',
        }
      : {
          // Dark mode colors - Vibrant Dark Theme
          primary: {
            main: '#60a5fa', // Brighter Blue
            light: '#93c5fd',
            dark: '#3b82f6',
            contrastText: '#000000',
          },
          secondary: {
            main: '#a78bfa', // Bright Purple
            light: '#c4b5fd',
            dark: '#8b5cf6',
            contrastText: '#000000',
          },
          success: {
            main: '#22c55e',
            light: '#4ade80',
            dark: '#16a34a',
            contrastText: '#ffffff',
          },
          warning: {
            main: '#f59e0b',
            light: '#fbbf24',
            dark: '#d97706',
            contrastText: '#000000',
          },
          error: {
            main: '#ef4444',
            light: '#f87171',
            dark: '#dc2626',
            contrastText: '#ffffff',
          },
          info: {
            main: '#06b6d4',
            light: '#22d3ee',
            dark: '#0891b2',
            contrastText: '#ffffff',
          },
          background: {
            default: '#0f172a', // Deep Slate
            paper: '#1e293b', // Lighter Slate for cards
          },
          text: {
            primary: '#f1f5f9',
            secondary: '#cbd5e1',
          },
          divider: '#334155',
        }),
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 10,
          fontWeight: 600,
          padding: '10px 24px',
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: mode === 'light'
              ? '0 8px 16px rgba(0, 102, 204, 0.2)'
              : '0 8px 16px rgba(59, 130, 246, 0.3)',
          },
        },
        contained: {
          background: mode === 'light'
            ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
            : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
          '&:hover': {
            background: mode === 'light'
              ? 'linear-gradient(135deg, #4b5563 0%, #374151 100%)'
              : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: mode === 'light' ? 'rgba(0, 102, 204, 0.04)' : 'rgba(59, 130, 246, 0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: mode === 'light'
            ? '0 4px 20px rgba(15, 23, 42, 0.08)'
            : '0 4px 20px rgba(0, 0, 0, 0.5)',
          border: mode === 'light' 
            ? '1px solid rgba(226, 232, 240, 0.8)'
            : '1px solid rgba(51, 65, 85, 0.6)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: mode === 'light'
              ? '0 12px 40px rgba(0, 102, 204, 0.15)'
              : '0 12px 40px rgba(59, 130, 246, 0.25)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundImage: 'none',
          border: mode === 'light' 
            ? '1px solid rgba(226, 232, 240, 0.6)'
            : '1px solid rgba(51, 65, 85, 0.4)',
        },
        elevation1: {
          boxShadow: mode === 'light'
            ? '0 2px 8px rgba(15, 23, 42, 0.06)'
            : '0 2px 8px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
          height: 28,
        },
        colorPrimary: {
          backgroundColor: mode === 'light' ? '#dbeafe' : '#1e3a8a',
          color: mode === 'light' ? '#1e40af' : '#93c5fd',
        },
        colorSecondary: {
          backgroundColor: mode === 'light' ? '#d1fae5' : '#064e3b',
          color: mode === 'light' ? '#065f46' : '#6ee7b7',
        },
        colorSuccess: {
          backgroundColor: mode === 'light' ? '#dcfce7' : '#14532d',
          color: mode === 'light' ? '#166534' : '#86efac',
        },
        colorWarning: {
          backgroundColor: mode === 'light' ? '#fed7aa' : '#7c2d12',
          color: mode === 'light' ? '#9a3412' : '#fdba74',
        },
        colorError: {
          backgroundColor: mode === 'light' ? '#fee2e2' : '#7f1d1d',
          color: mode === 'light' ? '#991b1b' : '#fca5a5',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: mode === 'light'
            ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
            : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          boxShadow: mode === 'light'
            ? '0 4px 20px rgba(107, 114, 128, 0.2)'
            : '0 4px 20px rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(10px)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: mode === 'light' ? '#0066cc' : '#3b82f6',
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
                borderColor: mode === 'light' ? '#0066cc' : '#3b82f6',
              },
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.1)',
            backgroundColor: mode === 'light' ? 'rgba(0, 102, 204, 0.08)' : 'rgba(59, 130, 246, 0.12)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: mode === 'light' 
            ? '1px solid rgba(226, 232, 240, 0.8)'
            : '1px solid rgba(51, 65, 85, 0.6)',
        },
        head: {
          fontWeight: 700,
          backgroundColor: mode === 'light' ? '#f8fafc' : '#1e293b',
        },
      },
    },
  },
});

export const createAppTheme = (mode: 'light' | 'dark') => {
  return createTheme(getDesignTokens(mode));
};